// AuthContext.js
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useCommonStore } from "stores/common-store";
import { UserType } from "utils/constants";
import loadReferenceData from "./ReferenceData";
import { updateLastLoginTime } from "utils";
import { JA } from "utils/strings.ja";
import { setLoginNotice } from "utils/loginNotice";
import CustomLoading from "components/CustomLoading";

const AuthContext = createContext();

function getInitialStateLocal() {
  const data = localStorage.getItem("dataLogin");
  return data ? JSON.parse(data) : { isLogin: false };
}

function setStateLocal(data) {
  localStorage.setItem("dataLogin", JSON.stringify(data));
}

export const AuthProvider = ({ children }) => {
  const [loginData, setLoginData] = useState(getInitialStateLocal);
  const [isLoading, setIsLoading] = useState(true);

  const {
    _hasHydrated,
    setServiceProviders,
    setTenants,
    setLocations,
    setSpTypeLogin,
    clearAllStore,
  } = useCommonStore((state) => state);

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", async ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          console.log("Sign in successful!");
          break;
        case "signInWithRedirect_failure":
          console.error("An error has ocurred during the OAuth flow.");
          break;
        case "tokenRefresh":
          const data_login = await accountLogin();
          if (!data_login) {
            await endSession();
            break;
          }
          const updateDataLogin = { isLogin: true, ...data_login };
          setLoginData(updateDataLogin);
          console.log("force ----- auth tokens have been refreshed.");
          break;
        case "tokenRefresh_failure":
          // the refresh token is gone or expired: nothing will load until the
          // user signs in again, so send them to the login screen
          console.log("failure while refreshing auth tokens.");
          await endSession();
          break;
        // case "customOAuthState":
        //   console.log(payload.data);
        //   setCustomState(payload.data); // this is the customState provided on signInWithRedirect function
        //   break;
      }
    });
    return unsubscribe
  }, []);

  useEffect(() => {
    setStateLocal(loginData);
  }, [loginData]);

  useEffect(() => {
    if (_hasHydrated) {
      if (loginData.isLogin) {
        restoreSession();
      } else {
        updateLoading(false);
      }
    }
  }, [_hasHydrated]);

  // On app load with a remembered login: `dataLogin` in localStorage only says
  // the user signed in once — the Cognito session behind it may have ended
  // since (refresh token expired after 30 days, user deleted or signed out
  // everywhere). Without a session every request fails with "No current user"
  // and the pages just come up empty, so check it first. A live session also
  // refreshes the login data from the ID token, in case its claims changed.
  const restoreSession = async () => {
    const data_login = await accountLogin();
    if (!data_login?.groups) {
      await endSession();
      return;
    }
    const updateDataLogin = { isLogin: true, ...data_login };
    setLoginData(updateDataLogin);
    await fetchCommonData(updateDataLogin);
  };

  // Forget a login whose Cognito session is gone; SidebarLayout then redirects
  // to the login screen. signOut clears whatever Amplify still holds locally.
  const endSession = async () => {
    setLoginData({ isLogin: false });
    clearAllStore();
    try {
      await signOut();
    } catch (error) {
      console.log("error clearing the ended session: ", error);
    }
    updateLoading(false);
  };

  // Login data from the current ID token, or null when there is no session.
  const accountLogin = async () => {
    let authSession;
    try {
      authSession = await fetchAuthSession();
    } catch (error) {
      console.log("no auth session: ", error);
      return null;
    }
    const tokens = authSession.tokens;
    const payload_auth = tokens?.idToken?.payload;
    if (!payload_auth) return null;

    // set login data
    const data_login = {
      auth_time: payload_auth.auth_time,
      last_login: payload_auth.birthdate,
    };

    data_login["expired"] = payload_auth["exp"] || null;
    data_login["username"] = payload_auth["cognito:username"];
    data_login["groups"] = payload_auth["cognito:groups"];

    if (!payload_auth.profile) {
      return data_login;
    }

    let profile = JSON.parse(payload_auth.profile);

    data_login["profileName"] = profile.profileName;
    data_login["spID"] = profile.spID;
    data_login["mfa"] = profile.mfa;
    data_login["otp"] = profile.mfa ? "Enabled" : "Disabled";
    data_login["role"] = profile.role;
    data_login["type"] = profile.type;

    if (profile.type === UserType.Tenant) {
      data_login["tenantID"] = profile.tenantID;
    }

    if (profile.type === UserType.Location) {
      data_login["tenantID"] = profile.tenantID;
      data_login["locationID"] = profile.locationID;
    }

    return data_login;
  };

  const login = async () => {
    const data_login = await accountLogin();

    if (!data_login?.groups) {
      setLoginNotice(JA.user.notInGroup, "error");
      try {
        logout();
        window.location.reload(false);
      } catch (error) {
        console.log("error signing out: ", error);
      } finally {
        setLoginData({ isLogin: false });
      }
    } else {
      const updateDataLogin = { isLogin: true, ...data_login };
      setLoginData(updateDataLogin);
      // update data into store
      await fetchCommonData(updateDataLogin);
    }
  };

  // Seed the common store with the SPs, tenants and locations this caller may
  // see — the lists pages read names and dropdown choices from.
  const fetchCommonData = async (updateDataLogin = null) => {
    const { serviceProviders, tenants, locations, spType } =
      await loadReferenceData(updateDataLogin);

    setServiceProviders(serviceProviders);
    setTenants(tenants);
    setLocations(locations);
    setSpTypeLogin(spType);

    updateLoading(false);
  };

  const logout = async () => {
    if (loginData.auth_time) {
      await updateLastLoginTime(loginData.auth_time.toString());
    }
    setLoginData({ isLogin: false });
    clearAllStore();
    await signOut();
  };

  const updateLoading = (val) => {
    setIsLoading(val);
  };

  return (
    <AuthContext.Provider
      value={{
        loginData,
        login,
        logout,
        isLoading,
      }}
    >
      {isLoading ? (
        <CustomLoading />
      ) : (
        <>{children}</>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
